<!--
  NOTE: This is a placeholder for the @/components/Partner/PartnerEmploymentsTable component.
  The real one will be built with @/components/GraphqlDataTable/GraphqlDataTable.vue
-->
<template>
  <div class="card card-white">
    <div class="tw-flex tw-items-center tw-justify-between tw-py-2 tw-pr-0 tw-pl-0 tw-border-b tw-border-gray-200">
      <!-- Left side: Search and filter dropdown -->
      <div class="tw-flex tw-items-center tw-gap-3 tw-pl-0">
        <!-- Search input using WdsInput component -->
        <div class="tw-relative tw-w-[250px] tw-pl-0">
          <WdsInput
            v-model="searchQuery"
            class="tw-w-full tw-h-8 tw-text-base tw-text-gray-900"
            type="text"
            prefix-icon="search"
            :placeholder="$t('Search name, email, title...')"
          >
            <WdsIcon
              v-if="searchQuery"
              :size="16"
              class="tw-cursor-pointer tw-text-gray-500"
              name="close-filled"
              @click="searchQuery = ''"
            />
          </WdsInput>
        </div>

        <!-- Filter button -->
        <div class="tw-relative">
          <PartnerFilterDropdown
            v-model="filters"
            :filter-groups="filterGroups"
            class="tw-ml-2 tw-text-base tw-text-gray-900"
            @apply="applyFilters"
          />
        </div>
      </div>

      <!-- Right side: Items Count, Sort, Export -->
      <div class="tw-flex tw-items-center">
        <!-- Total count -->
        <span class="tw-text-base tw-text-gray-900 tw-mr-1">{{ filteredEmployments.length }} items</span>

        <!-- Vertical divider -->
        <div class="vertical-divider tw-mx-1"></div>

        <!-- Sort button with text and icon -->
        <button type="button" class="tw-appearance-none tw-bg-transparent tw-border-0 tw-p-1 tw-cursor-pointer">
          <span class="tw-flex tw-items-center tw-text-base tw-text-gray-900">
            <WdsIcon name="arrows" :size="16" class="tw-mr-1 tw-text-gray-900" weight="thick" />
            {{ $t('Sort') }}
          </span>
        </button>

        <!-- Vertical divider -->
        <div class="vertical-divider tw-mx-1"></div>

        <!-- Export button with icon only, right-flushed -->
        <button type="button" class="tw-appearance-none tw-bg-transparent tw-border-0 tw-p-1 tw-cursor-pointer tw-ml-auto">
          <WdsIcon name="download" :size="16" class="tw-text-gray-900" />
        </button>
      </div>
    </div>

    <BaseDataTable
      :columns="columns"
      :entries="filteredEmployments"
      tall-rows
      :search-disabled="true"
      class="partner-table"
      header-class="tw-font-medium tw-text-gray-600 tw-py-1"
      row-class="hover:tw-bg-gray-50"
    >
      <template #no-results>
        <div class="tw-justify-center" data-test="no-employments-found">
          {{ $t('No employments found') }}
        </div>
      </template>

      <!-- Worker column with name, email, and initials -->
      <template #WorkerNameAndEmail="{ row }">
        <WdsStack align-center gap-md>
          <div>
            <WdsText class="tw-font-medium">{{ row.worker.name }}</WdsText>
            <WdsText secondary>{{ row.worker.email }}</WdsText>
          </div>
        </WdsStack>
      </template>

      <!-- Job column with title and ID -->
      <template #JobTitleAndID="{ row }">
        <div>
          <WdsText class="tw-font-medium">{{ row.job.title }}</WdsText>
          <WdsText secondary>{{ row.job.id }}</WdsText>
        </div>
      </template>

      <!-- Contract type -->
      <template #Contract>
        <WdsIcon name="document" :size="24" />
      </template>

      <!-- Start date -->
      <template #StartDate="{ row }">
        <WdsText>{{ formatDate(row.startDate) }}</WdsText>
      </template>

      <!-- End date -->
      <template #EndDate="{ row }">
        <WdsText>{{ formatDate(row.endDate) }}</WdsText>
      </template>

      <!-- Previously hired status -->
      <template #PreviouslyHired="{ row }">
        <WdsText>{{ row.previouslyHired ? $t('Yes') : $t('No') }}</WdsText>
      </template>

      <!-- Freelancer location -->
      <template #FreelancerLocation="{ row }">
        <WdsText>{{ row.worker.location }}</WdsText>
      </template>

      <!-- Assignment company -->
      <template #AssignmentCompany="{ row }">
        <WdsText>{{ row.assignmentCompany }}</WdsText>
      </template>

      <!-- Hiring manager -->
      <template #HiringManager="{ row }">
        <WdsText>{{ row.hiringManager }}</WdsText>
      </template>

      <!-- Actions column -->
      <template #Actions>
        <WdsMenu placement="bottom-end">
          <template #trigger>
            <WdsButtonGhost :label="$t('More options')" data-test="employment-actions-menu">
              <WdsIcon name="overflow-menu-horizontal" />
            </WdsButtonGhost>
          </template>
          <WdsMenuOption @click="() => {}">
            {{ $t('View details') }}
          </WdsMenuOption>
          <WdsMenuOption @click="() => {}">
            {{ $t('Download') }}
          </WdsMenuOption>
        </WdsMenu>
      </template>
    </BaseDataTable>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  WdsButtonGhost,
  WdsIcon,
  WdsMenu,
  WdsMenuOption,
  WdsStack,
  WdsText,
  WdsInput
} from '@worksome/wds-components'
import BaseDataTable from '@/components/BaseDataTable/BaseDataTable.vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import PartnerFilterDropdown from '@/components/Partner/PartnerFilterDropdown.vue'

// Define the FilterGroup type to match the PartnerFilterDropdown component's expected props
interface FilterOption {
  label: string;
  value: string | number | boolean;
}

interface FilterGroup {
  key: string;
  title: string;
  type: 'checkboxes' | 'select' | 'dateRange';
  options?: FilterOption[];
}

const props = defineProps<{
  employments: Array<{
    id: string;
    worker: {
      name: string;
      email: string;
      location: string;
    };
    job: {
      title: string;
      id: string;
    };
    contract: string;
    startDate: string;
    endDate: string;
    previouslyHired: boolean;
    assignmentCompany: string;
    hiringManager?: string;
  }>;
}>();

// Search functionality
const searchQuery = ref('');

// Filter functionality
interface FilterValues {
  previouslyHired?: boolean[];
  locations?: string[];
  companies?: string[];
  dateRange?: {
    from?: string;
    to?: string;
  };
  [key: string]: any;
}

const filters = ref<FilterValues>({});

// Filter groups definition for the filter dropdown
const filterGroups: FilterGroup[] = [
  {
    key: 'previouslyHired',
    title: $t('Previously hired'),
    type: 'checkboxes',
    options: [
      { label: $t('Yes'), value: true },
      { label: $t('No'), value: false }
    ]
  },
  {
    key: 'locations',
    title: $t('Freelancer location'),
    type: 'select',
    options: [
      { label: 'United States', value: 'US' },
      { label: 'United Kingdom', value: 'UK' },
      { label: 'Denmark', value: 'DK' },
      { label: 'Germany', value: 'DE' },
      { label: 'Canada', value: 'CA' }
    ]
  },
  {
    key: 'companies',
    title: $t('Assignment Company'),
    type: 'select',
    options: [
      { label: 'Worksome Inc', value: 'Worksome Inc' },
      { label: 'Worksome UK Ltd', value: 'Worksome UK Ltd' },
      { label: 'Worksome ApS', value: 'Worksome ApS' }
    ]
  },
  {
    key: 'dateRange',
    title: $t('Date range'),
    type: 'dateRange'
  },
];

// Filter employments based on filters
const applyFilters = () => {
  // The filtering will be applied via the filteredEmployments computed property
};

// Filter employments based on search query and filters
const filteredEmployments = computed(() => {
  let results = props.employments;

  // Apply search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    results = results.filter(employment => {
      // Search in worker name and email
      if (employment.worker.name.toLowerCase().includes(query) ||
          employment.worker.email.toLowerCase().includes(query)) {
        return true;
      }

      // Search in job title and ID
      if (employment.job.title.toLowerCase().includes(query) ||
          employment.job.id.toLowerCase().includes(query)) {
        return true;
      }

      // Search in assignment company
      if (employment.assignmentCompany.toLowerCase().includes(query)) {
        return true;
      }

      // Search in location
      if (employment.worker.location.toLowerCase().includes(query)) {
        return true;
      }

      // Search in hiring manager if available
      if (employment.hiringManager && employment.hiringManager.toLowerCase().includes(query)) {
        return true;
      }

      return false;
    });
  }

  // Apply filters
  if (filters.value.previouslyHired && filters.value.previouslyHired.length > 0) {
    results = results.filter(employment =>
      filters.value.previouslyHired?.includes(employment.previouslyHired));
  }

  if (filters.value.locations && filters.value.locations.length > 0) {
    // For demo purposes we're just checking if the location string contains the country code
    results = results.filter(employment =>
      filters.value.locations?.some(location =>
        employment.worker.location.includes(location)));
  }

  if (filters.value.companies && filters.value.companies.length > 0) {
    results = results.filter(employment =>
      filters.value.companies?.includes(employment.assignmentCompany));
  }

  if (filters.value.dateRange) {
    const { from, to } = filters.value.dateRange;

    if (from) {
      const fromDate = new Date(from);
      results = results.filter(employment =>
        new Date(employment.startDate) >= fromDate);
    }

    if (to) {
      const toDate = new Date(to);
      results = results.filter(employment =>
        new Date(employment.endDate) <= toDate);
    }
  }

  return results;
});

// Table columns configuration
const columns = computed(() => [
  {
    key: 'worker.name',
    title: $t('Worker name and email'),
    slot: 'WorkerNameAndEmail',
    orderAble: true,
    width: '220px',
  },
  {
    key: 'job.title',
    title: $t('Job title and Worksome Job ID'),
    slot: 'JobTitleAndID',
    orderAble: true,
    width: '200px',
  },
  {
    key: 'contract',
    title: $t('Contract'),
    slot: 'Contract',
    orderAble: true,
    width: '100px',
  },
  {
    key: 'startDate',
    title: $t('Start date'),
    slot: 'StartDate',
    orderAble: true,
    width: '110px',
  },
  {
    key: 'endDate',
    title: $t('End date'),
    slot: 'EndDate',
    orderAble: true,
    width: '110px',
  },
  {
    key: 'previouslyHired',
    title: $t('Previously hired'),
    slot: 'PreviouslyHired',
    orderAble: true,
    width: '130px',
  },
  {
    key: 'worker.location',
    title: $t('Freelancer location'),
    slot: 'FreelancerLocation',
    orderAble: true,
    width: '150px',
  },
  {
    key: 'assignmentCompany',
    title: $t('Assignment Company'),
    slot: 'AssignmentCompany',
    orderAble: true,
    width: '180px',
  },
  {
    key: 'hiringManager',
    title: $t('Hiring manager'),
    slot: 'HiringManager',
    orderAble: true,
    width: '150px',
  },
  {
    key: 'actions',
    title: $t('Actions'),
    slot: 'Actions',
    orderAble: false,
    sticky: true,
    width: '60px',
    classes: 'tw-bg-white',
  },
]);

// Helper functions
const getInitials = (name: string): string => name
  .split(' ')
  .map(part => part.charAt(0))
  .join('')
  .toUpperCase();

const formatDate = (dateString: string): string => new Date(dateString).toLocaleDateString();
</script>

<style scoped>
.partner-table :deep(tbody td) {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.partner-table :deep(th) {
  padding-top: 0.375rem;
  padding-bottom: 0.375rem;
  font-weight: 500;
}

/* Reduce spacing in stacked content */
.partner-table :deep(.wds-stack) {
  gap: 0.25rem !important;
}

/* Make elements more compact */
.partner-table :deep(.wds-text + .wds-text) {
  margin-top: 0;
}

/* Vertical divider styling */
.vertical-divider {
  display: inline-block;
  height: 18px;
  border-right: 1px solid #d1d5db; /* gray-300 */
  vertical-align: middle;
}
</style>
